import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaComments, FaBrain, FaShieldAlt, FaClock, FaUserLock, FaCheck, FaStar } from 'react-icons/fa';
import HowItWorks from '../components/HowItWorks';
import UserCategories from '../components/UserCategories';
import HelpCategories from '../components/HelpCategories';
import FAQ from '../components/FAQ';
import Partners from '../components/Partners';
import ContactForm from '../components/ContactForm';
import { motion } from 'framer-motion';

const HomePage = () => {
  const { t } = useTranslation();
  const { language } = useParams();
  const messagesEndRef = useRef(null);
  const chatMessagesContainerRef = useRef(null);
  const [visibleMessages, setVisibleMessages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [typing, setTyping] = useState(false);
  const [resetAnimation, setResetAnimation] = useState(false);
  const [isPhoneHovered, setIsPhoneHovered] = useState(false);
  const [isMessageAnimating, setIsMessageAnimating] = useState(false);
  const sectionRefs = useRef([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const testimonialsScrollRef = useRef(null);
  const [activeTestimonialIndex, setActiveTestimonialIndex] = useState(0);
  const [typedCode, setTypedCode] = useState('');
  const [typedDescription, setTypedDescription] = useState('');
  const [isTypingComplete, setIsTypingComplete] = useState(false);

  const features = [
    { icon: <FaShieldAlt />, text: t('home.features_list.secure_encryption') },
    { icon: <FaClock />, text: t('home.features_list.availability_24_7') },
    { icon: <FaBrain />, text: t('home.features_list.advanced_ai') },
    { icon: <FaUserLock />, text: t('home.features_list.full_confidentiality') }
  ];

  // Мок для відгуків користувачів
  const testimonials = useMemo(() => [
    {
      name: t('home.testimonials.users.0.name'),
      role: t('home.testimonial_roles.civilian'),
      text: t('home.testimonials.users.0.text'),
      avatar: "/assets/pic/testimonial-1.jpg",
      rating: 5
    },
    {
      name: t('home.testimonials.users.1.name'),
      role: t('home.testimonial_roles.military'),
      text: t('home.testimonials.users.1.text'),
      avatar: "/assets/pic/testimonial-2.jpg",
      rating: 5
    },
    {
      name: t('home.testimonials.users.2.name'),
      role: t('home.testimonial_roles.medical'),
      text: t('home.testimonials.users.2.text'),
      avatar: "/assets/pic/testimonial-3.jpg",
      rating: 5
    },
    {
      name: t('home.testimonials.users.3.name'),
      role: t('home.testimonial_roles.student'),
      text: t('home.testimonials.users.3.text'),
      avatar: "/assets/pic/testimonial-4.jpg",
      rating: 4
    },
    {
      name: t('home.testimonials.users.4.name'),
      role: t('home.testimonial_roles.entrepreneur'),
      text: t('home.testimonials.users.4.text'),
      avatar: "/assets/pic/testimonial-5.jpg",
      rating: 5
    },
    {
      name: t('home.testimonials.users.5.name'),
      role: t('home.testimonial_roles.veteran'),
      text: t('home.testimonials.users.5.text'),
      avatar: "/assets/pic/testimonial-6.jpg",
      rating: 5
    },
    {
      name: t('home.testimonials.users.6.name'),
      role: t('home.testimonial_roles.teacher'),
      text: t('home.testimonials.users.6.text'),
      avatar: "/assets/pic/testimonial-7.jpg",
      rating: 4
    },
    {
      name: t('home.testimonials.users.7.name'),
      role: t('home.testimonial_roles.it_specialist'),
      text: t('home.testimonials.users.7.text'),
      avatar: "/assets/pic/testimonial-8.jpg",
      rating: 5
    },
    {
      name: t('home.testimonials.users.8.name'),
      role: t('home.testimonial_roles.young_mother'),
      text: t('home.testimonials.users.8.text'),
      avatar: "/assets/pic/testimonial-9.jpg",
      rating: 5
    },
    {
      name: t('home.testimonials.users.9.name'),
      role: t('home.testimonial_roles.athlete'),
      text: t('home.testimonials.users.9.text'),
      avatar: "/assets/pic/testimonial-10.jpg",
      rating: 5
    }
  ], [t]);

  // Діалог для демонстрації проблеми стресу (10 запитань користувача та 10 відповідей від AI)
  const chatMessages = useMemo(() => [
    { sender: 'user', text: t('home.chat_messages.0.user') },
    { sender: 'ai', text: t('home.chat_messages.0.ai') },
    
    { sender: 'user', text: t('home.chat_messages.1.user') },
    { sender: 'ai', text: t('home.chat_messages.1.ai') },
    
    { sender: 'user', text: t('home.chat_messages.2.user') },
    { sender: 'ai', text: t('home.chat_messages.2.ai') },
    
    { sender: 'user', text: t('home.chat_messages.3.user') },
    { sender: 'ai', text: t('home.chat_messages.3.ai') },
    
    { sender: 'user', text: t('home.chat_messages.4.user') },
    { sender: 'ai', text: t('home.chat_messages.4.ai') },
    
    { sender: 'user', text: t('home.chat_messages.5.user') },
    { sender: 'ai', text: t('home.chat_messages.5.ai') },
    
    { sender: 'user', text: t('home.chat_messages.6.user') },
    { sender: 'ai', text: t('home.chat_messages.6.ai') },
    
    { sender: 'user', text: t('home.chat_messages.7.user') },
    { sender: 'ai', text: t('home.chat_messages.7.ai') },
    
    { sender: 'user', text: t('home.chat_messages.8.user') },
    { sender: 'ai', text: t('home.chat_messages.8.ai') }
  ], [t]);

  // Функція для скидання чату
  const resetChat = useCallback(() => {
    setResetAnimation(true);
    setTimeout(() => {
      setVisibleMessages([]);
      setCurrentIndex(0);
      setResetAnimation(false);
      setIsMessageAnimating(false);
    }, 1000);
  }, []);

  // Функція для додавання наступного повідомлення з ефектом набору тексту
  const addNextMessage = useCallback(() => {
    if (currentIndex < chatMessages.length) {
      const nextMessage = chatMessages[currentIndex];
      
      // Встановлюємо прапорець анімації
      setIsMessageAnimating(true);
      
      // Додаємо повідомлення залежно від відправника
      if (nextMessage.sender === 'ai') {
        // Для AI показуємо спочатку анімацію набору тексту
        setTyping(true);
        setTimeout(() => {
          setTyping(false);
          setVisibleMessages(prevMessages => [...prevMessages, nextMessage]);
          setCurrentIndex(prevIndex => prevIndex + 1);
          
          // Скролимо тільки контейнер чату
          setTimeout(() => {
            if (chatMessagesContainerRef.current) {
              chatMessagesContainerRef.current.scrollTop = chatMessagesContainerRef.current.scrollHeight;
              
              // Знімаємо прапорець анімації після завершення
              setTimeout(() => {
                setIsMessageAnimating(false);
              }, 500);
            }
          }, 100);
        }, 1500);
      } else {
        // Для користувача одразу додаємо повідомлення
        setVisibleMessages(prevMessages => [...prevMessages, nextMessage]);
        setCurrentIndex(prevIndex => prevIndex + 1);
        
        // Скролимо тільки контейнер чату
        setTimeout(() => {
          if (chatMessagesContainerRef.current) {
            chatMessagesContainerRef.current.scrollTop = chatMessagesContainerRef.current.scrollHeight;
            
            // Знімаємо прапорець анімації після завершення
            setTimeout(() => {
              setIsMessageAnimating(false);
            }, 500);
          }
        }, 100);
      }
    } else {
      // Коли досягли кінця, скидаємо чат
      resetChat();
    }
  }, [currentIndex, chatMessages, resetChat]);

  // Додавання першого повідомлення при завантаженні
  useEffect(() => {
    if (visibleMessages.length === 0 && !typing && !resetAnimation && !isMessageAnimating) {
      // Додаємо більшу затримку перед першим повідомленням, щоб воно не з'являлося миттєво
      const initialTimer = setTimeout(() => {
        addNextMessage();
      }, 2500);
      
      return () => clearTimeout(initialTimer);
    }
  }, [visibleMessages, typing, resetAnimation, addNextMessage, isMessageAnimating]);

  // Додавання наступного повідомлення після попереднього
  useEffect(() => {
    if (visibleMessages.length > 0 && !typing && currentIndex < chatMessages.length && !resetAnimation && !isMessageAnimating) {
      // Додаємо різну затримку залежно від типу останнього повідомлення
      const lastMessage = visibleMessages[visibleMessages.length - 1];
      const delay = lastMessage.sender === 'user' ? 1500 : 2500; // Швидша відповідь AI після користувача
      
      const timer = setTimeout(() => {
        addNextMessage();
      }, delay);
      
      return () => clearTimeout(timer);
    }
  }, [visibleMessages, typing, currentIndex, chatMessages, addNextMessage, resetAnimation, isMessageAnimating]);

  // Налаштовуємо прокручування і обробку колеса миші тільки для контейнера чату
  useEffect(() => {
    const container = chatMessagesContainerRef.current;
    
    if (container && visibleMessages.length > 0) {
      // Включаємо скролінг тільки якщо повідомлень більше 3
      if (visibleMessages.length > 3) {
        container.classList.add('show-scroll');
      } else {
        container.classList.remove('show-scroll');
      }
      
      // Функція для обробки події колеса миші
      const handleWheel = (e) => {
        // Перевіряємо, чи потрібен скролінг
        if (container.scrollHeight > container.clientHeight) {
          const scrollTop = container.scrollTop;
          const scrollHeight = container.scrollHeight;
          const height = container.clientHeight;
          
          const scrolledToTop = scrollTop <= 0;
          const scrolledToBottom = scrollTop + height >= scrollHeight - 5;
          
          // Запобігаємо скролінгу сторінки, коли курсор над контейнером чату
          // але дозволяємо скролінг сторінки, якщо досягнуто верхньої або нижньої межі
          if ((e.deltaY < 0 && !scrolledToTop) || (e.deltaY > 0 && !scrolledToBottom)) {
            e.preventDefault();
            e.stopPropagation();
            
            // Плавне прокручування контейнера
            container.scrollBy({
              top: e.deltaY < 0 ? -30 : 30,
              behavior: 'smooth'
            });
          }
        }
      };
      
      // Додаємо обробник події
      container.addEventListener('wheel', handleWheel, { passive: false });
      
      // Скролимо до останнього повідомлення
      container.scrollTop = container.scrollHeight;
      
      return () => {
        container.removeEventListener('wheel', handleWheel);
      };
    }
  }, [visibleMessages]);

  // Функція для додавання елементів до масиву посилань
  const addToRefs = (el) => {
    if (el && !sectionRefs.current.includes(el)) {
      sectionRefs.current.push(el);
    }
  };
  
  // Функція для перевірки, чи елемент видимий у вьюпорті
  const isElementInViewport = (el) => {
    const rect = el.getBoundingClientRect();
    return (
      rect.top <= (window.innerHeight || document.documentElement.clientHeight) * 0.8 &&
      rect.bottom >= 0
    );
  };
  
  // Функція для обробки скролінгу з оптимізацією продуктивності
  const handleScroll = useCallback(() => {
    if (!sectionRefs.current.length) return;
    
    // Використовуємо requestAnimationFrame для оптимізації
    window.requestAnimationFrame(() => {
      sectionRefs.current.forEach((section) => {
        if (section && isElementInViewport(section)) {
          section.classList.add('visible');
        }
      });
    });
  }, []);
  
  // Додаємо обробник скролінгу з дебаунсингом
  useEffect(() => {
    let scrollTimeout;
    
    const debouncedScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      scrollTimeout = setTimeout(() => {
        handleScroll();
      }, 10);
    };
    
    window.addEventListener('scroll', debouncedScroll);
    window.addEventListener('resize', debouncedScroll);
    
    // Викликаємо функцію один раз для перевірки елементів, які вже видимі
    setTimeout(handleScroll, 300);
    
    return () => {
      window.removeEventListener('scroll', debouncedScroll);
      window.removeEventListener('resize', debouncedScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [handleScroll]);
  
  // Встановлюємо прапорець завершення початкового завантаження
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  const codeExample = t('home.code_section.code_example');

  const description = {
    title: t('home.code_section.trust_question'),
    text: t('home.code_section.trust_answer')
  };

  // Оновлена функція анімації набору тексту
  const startTypingAnimation = useCallback(() => {
    let codeTimeout;
    let descTimeout;
    let currentIndex = 0;
    let currentDescIndex = 0;

    const typeCode = () => {
      if (currentIndex <= codeExample.length) {
        setTypedCode(codeExample.slice(0, currentIndex));
        currentIndex++;
        codeTimeout = setTimeout(typeCode, 30);
      } else {
        typeDescription();
      }
    };

    const typeDescription = () => {
      if (currentDescIndex <= description.text.length) {
        setTypedDescription(description.text.slice(0, currentDescIndex));
        currentDescIndex++;
        descTimeout = setTimeout(typeDescription, 30);
      } else {
        setIsTypingComplete(true);
        // Запускаємо нову анімацію через 15 секунд
        setTimeout(() => {
          setTypedCode('');
          setTypedDescription('');
          setIsTypingComplete(false);
          currentIndex = 0;
          currentDescIndex = 0;
          typeCode();
        }, 15000);
      }
    };

    typeCode();

    return () => {
      clearTimeout(codeTimeout);
      clearTimeout(descTimeout);
    };
  }, [codeExample, description.text]);

  // Запускаємо анімацію при монтуванні компонента
  useEffect(() => {
    const cleanup = startTypingAnimation();
    return () => cleanup();
  }, [startTypingAnimation]);

  const handleTestimonialScroll = (direction) => {
    const container = testimonialsScrollRef.current;
    if (container) {
      const scrollAmount = 400; // Ширина картки
      const newScrollPosition = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      
      // Оновлюємо активний індекс
      const newIndex = Math.round(newScrollPosition / scrollAmount);
      setActiveTestimonialIndex(Math.max(0, Math.min(newIndex, testimonials.length - 1)));
      
      container.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
    }
  };

  // Додаємо функцію для прокрутки до конкретного відгуку
  const scrollToTestimonial = (index) => {
    const container = testimonialsScrollRef.current;
    if (container) {
      const scrollAmount = 400 * index;
      container.scrollTo({
        left: scrollAmount,
        behavior: 'smooth'
      });
      setActiveTestimonialIndex(index);
    }
  };

  // Додаємо обробник прокрутки для оновлення активного індексу
  useEffect(() => {
    const container = testimonialsScrollRef.current;
    if (container) {
      const handleScroll = () => {
        const newIndex = Math.round(container.scrollLeft / 400);
        setActiveTestimonialIndex(newIndex);
      };
      
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Функція для створення локалізованого посилання
  const getLocalizedLink = (path) => {
    return `/${language}${path === '/' ? '' : path}`;
  };

  return (
    <div className={`home-page ${isInitialLoad ? 'initial-load' : ''}`}>
      <section className="hero-section">
        <div className="hero-background">
          <div className="gradient-circle-1"></div>
          <div className="gradient-circle-2"></div>
          <div className="hero-bg-grid"></div>
        </div>
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title" 
                dangerouslySetInnerHTML={{ __html: t('home.title') }}>
              </h1>
              <p className="hero-subtitle">
                {t('home.subtitle')}
              </p>
              
              <div className="hero-buttons">
                <a href="https://chat.psyhelp.info" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  {t('home.try_now')}
                  <FaComments />
                </a>
                <Link to={getLocalizedLink('/news')} className="btn btn-outline">
                  {t('home.learn_more')}
                </Link>
              </div>
              
              <div className="hero-stats">
                <div className="stat-item">
                  <div className="stat-number">24/7</div>
                  <div className="stat-label">{t('home.stats.availability')}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">100%</div>
                  <div className="stat-label">{t('home.stats.confidentiality')}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">50K+</div>
                  <div className="stat-label">{t('home.stats.active_users')}</div>
                </div>
              </div>
              
              <p className="hero-multilingual-support" data-aos="fade-up" data-aos-delay="200" dangerouslySetInnerHTML={{ __html: t('home.multilingual_support') }}></p>
              
            </div>

            <div className="hero-image">
              <div 
                className="phone-container"
                onMouseEnter={() => setIsPhoneHovered(true)}
                onMouseLeave={() => setIsPhoneHovered(false)}
              >
                <div className="phone-notch"></div>
                <div className="phone-button"></div>
                <div className="phone-screen">
                  <div className={`live-chat-container ${resetAnimation ? 'reset-chat' : ''} ${isPhoneHovered ? 'phone-hovered' : ''}`}>
                    <div className="chat-header">
                      <div className="avatar">AI</div>
                      <div className="chat-info">
                        <h4 className="chat-name">AI PSY HELP</h4>
                        <p className="chat-status">{t('home.chat.online')}</p>
                      </div>
                      <div className="header-icons">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M12 18C8.68629 18 6 15.3137 6 12C6 8.68629 8.68629 6 12 6C15.3137 6 18 8.68629 18 12C18 15.3137 15.3137 18 12 18ZM12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16ZM11 1H13V4H11V1ZM11 20H13V23H11V20ZM23 11V13H20V11H23ZM4 11V13H1V11H4Z"></path></svg>
                      </div>
                    </div>

                    <div className={`chat-messages ${visibleMessages.length > 3 ? 'show-scroll' : ''}`} ref={chatMessagesContainerRef} tabIndex="-1">
                      {visibleMessages.map((message, index) => (
                        <div 
                          key={index} 
                          className={`message message-${message.sender}`}
                          tabIndex="-1"
                        >
                          {message.text}
                          <div className="message-time">
                            {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      ))}
                      {typing && (
                        <div className="typing-animation">
                          <span className="dot"></span>
                          <span className="dot"></span>
                          <span className="dot"></span>
                        </div>
                      )}
                      <div className="messages-end-marker" ref={messagesEndRef}></div>
                    </div>

                    <div className="chat-input">
                      <input type="text" placeholder={t('home.chat.placeholder')} readOnly tabIndex="-1" />
                      <div className="send-button">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M1.94607 9.31543C1.42353 9.14125 1.4194 8.86022 1.95682 8.68108L21.043 2.31901C21.5715 2.14285 21.8746 2.43866 21.7265 2.95694L16.2733 22.0432C16.1223 22.5716 15.8177 22.59 15.5944 22.0876L11.9999 14L17.9999 6.00005L9.99992 12L1.94607 9.31543Z"></path></svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="phone-shadow"></div>
            </div>
          </div>
        </div>
      </section>

      <section className="code-section">
        <div className="container">
          <div className="code-block">
            <div className="terminal-header">
              <div className="terminal-button close"></div>
              <div className="terminal-button minimize"></div>
              <div className="terminal-button maximize"></div>
              <div className="terminal-title">{t('home.code_section.terminal_title')}</div>
            </div>
            <pre>
              <code>
                {typedCode}
                {!isTypingComplete && <span className="typing-cursor" />}
              </code>
            </pre>
          </div>
          <div className="description-block">
            <h2>{description.title}</h2>
            <p>
              {typedDescription}
              {!isTypingComplete && <span className="typing-cursor" />}
            </p>
            <ul className="feature-list">
              {features.map((feature, index) => (
                <li key={index} style={{"--item-index": index}}>
                  <div className="icon">{feature.icon}</div>
                  <span>{feature.text}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <HowItWorks />
      <UserCategories />
      
      {/* Pricing Section */}
      <section className="pricing-section">
        <div className="container">
          <h2 className="section-title" ref={addToRefs}>{t('home.pricing.title')}</h2>
          <p className="section-description" ref={addToRefs}>
            {t('home.pricing.subtitle')}
          </p>
          
          <div className="pricing-grid">
            {/* Personal Plan */}
            <div className="pricing-card" data-plan="personal">
              <div className="pricing-card-header">
                <div className="badge-container">
                  <span className="support-note">{t('home.pricing.packages.personal.support_note')}</span>
                </div>
                <h3 className="plan-name">{t('home.pricing.packages.personal.name')}</h3>
                <div className="price-container">
                  <div className="price-intro">
                    <span className="price">{t('home.pricing.packages.personal.price_intro')}</span>
                    <span className="period">/ {t('home.pricing.packages.personal.intro_period')}</span>
                  </div>
                  <div className="price-regular">
                    <span className="price">{t('home.pricing.packages.personal.price_regular')}</span>
                  </div>
                </div>
              </div>
              
              <div className="pricing-card-features">
                <ul>
                  {(() => {
                    const features = t('home.pricing.packages.personal.features', { returnObjects: true });
                    if (Array.isArray(features)) {
                      return features.map((feature, index) => (
                        <li key={index}>
                          <FaCheck className="feature-icon" />
                          <span>{feature}</span>
                        </li>
                      ));
                    } else {
                      // Fallback features if translation doesn't return an array
                      const fallbackFeatures = [
                        "Необмежені діалоги з ШІ 24/7",
                        "Голосовий режим спілкування",
                        "Персоналізовані рекомендації",
                        "Доступ до всіх мов (UK, EN, DE, LT)",
                        "Техніки керування стресом",
                        "Без реклами",
                        "Повна конфіденційність"
                      ];
                      
                      return fallbackFeatures.map((feature, index) => (
                        <li key={index}>
                          <FaCheck className="feature-icon" />
                          <span>{feature}</span>
                        </li>
                      ));
                    }
                  })()}
                </ul>
              </div>
              
              <div className="pricing-card-cta">
                <a href="https://chat.psyhelp.info" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  {t('home.pricing.packages.personal.cta')}
                </a>
              </div>
            </div>
            
            {/* Family Plan */}
            <div className="pricing-card premium" data-plan="family">
              <div className="pricing-card-header">
                <div className="badge-container">
                  <span className="support-note">{t('home.pricing.packages.family.support_note')}</span>
                </div>
                <h3 className="plan-name">{t('home.pricing.packages.family.name')}</h3>
                <div className="price-container">
                  <div className="price-single">
                    <span className="price">{t('home.pricing.packages.family.price')}</span>
                    <span className="period">/ {t('home.pricing.monthly')}</span>
                  </div>
                  <div className="members-info">
                    {t('home.pricing.packages.family.members')}
                  </div>
                </div>
                <div className="coming-soon-badge">
                  {t('home.pricing.packages.family.soon')}
                </div>
              </div>
              
              <div className="pricing-card-features">
                <ul>
                  {(() => {
                    const features = t('home.pricing.packages.family.features', { returnObjects: true });
                    if (Array.isArray(features)) {
                      return features.map((feature, index) => (
                        <li key={index}>
                          <FaCheck className="feature-icon" />
                          <span>{feature}</span>
                        </li>
                      ));
                    } else {
                      // Fallback features if translation doesn't return an array
                      const fallbackFeatures = [
                        "Все з персонального плану",
                        "Доступ для 4-х членів родини",
                        "Сімейні програми підтримки",
                        "Пріоритетна підтримка",
                        "Розширені техніки медитації",
                        "Програми для батьків",
                        "Групові рекомендації"
                      ];
                      
                      return fallbackFeatures.map((feature, index) => (
                        <li key={index}>
                          <FaCheck className="feature-icon" />
                          <span>{feature}</span>
                        </li>
                      ));
                    }
                  })()}
                </ul>
              </div>
              
              <div className="pricing-card-cta">
                <span className="btn btn-primary disabled">
                  {t('home.pricing.packages.family.cta')}
                </span>
              </div>
            </div>
          </div>
          
          <div className="free-support-info">
            <div className="info-icon">
              <FaStar />
            </div>
            <div className="info-text">
              <p>{t('home.pricing.free_for_ukrainians')}</p>
              <p className="mission-statement">{t('home.pricing.mission_statement')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Відгуки користувачів */}
      <section className="testimonials-section">
        <div className="container">
          <h2 className="section-title" ref={addToRefs}>{t('home.testimonials.title')}</h2>
          <p className="section-description" ref={addToRefs}>
            {t('home.testimonials.description')}
          </p>
          
          <div className="testimonials-container">
            <button className="scroll-button prev" onClick={() => handleTestimonialScroll('left')}>{t('home.testimonials.scroll.prev')}</button>
            <div className="testimonials-scroll" ref={testimonialsScrollRef}>
              {testimonials.map((testimonial, index) => (
                <motion.div 
                  key={index}
                  className="testimonial-card"
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <div className="testimonial-content">
                    <div className="testimonial-rating">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="star">
                          {i < testimonial.rating ? '★' : '☆'}
                        </span>
                      ))}
                    </div>
                    <p className="testimonial-text">{testimonial.text}</p>
                    <div className="testimonial-author-info">
                      <div className="author-name">{testimonial.name}</div>
                      <div className="author-role">{testimonial.role}</div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <button className="scroll-button next" onClick={() => handleTestimonialScroll('right')}>{t('home.testimonials.scroll.next')}</button>
            
            {/* Додаємо індикатори прокрутки */}
            <div className="scroll-indicators">
              {testimonials.map((_, index) => (
                <div
                  key={index}
                  className={`indicator ${index === activeTestimonialIndex ? 'active' : ''}`}
                  onClick={() => scrollToTestimonial(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <HelpCategories />
      <FAQ />
      <Partners />
      <ContactForm />

      {/* Розділ CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content" ref={addToRefs}>
            <div className="cta-background">
              <div className="cta-bg-circle-1"></div>
              <div className="cta-bg-circle-2"></div>
              <div className="cta-bg-grid"></div>
            </div>
            <h2 className="cta-title">{t('home.cta_section.title')}</h2>
            <p className="cta-subtitle">{t('home.cta_section.subtitle')}</p>
            
            <div className="cta-pricing-offer">
              <div className="offer-badge">
                <div className="offer-badge-inner">
                  {t('home.pricing.packages.personal.price_intro')}
                </div>
              </div>
              <div className="offer-details">
                <div className="offer-title">{t('home.pricing.packages.personal.intro_period')}</div>
                <div className="offer-description">
                  {(() => {
                    const features = t('home.pricing.packages.personal.features', { returnObjects: true });
                    const feature1 = Array.isArray(features) && features.length > 0 ? features[0] : "Необмежені діалоги з ШІ 24/7";
                    const feature2 = Array.isArray(features) && features.length > 1 ? features[1] : "Голосовий режим спілкування";
                    return (
                      <>
                        {feature1} <span className="feature-separator">•</span> {feature2}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
            
            <div className="cta-buttons">
              <a href="https://chat.psyhelp.info" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                {t('home.pricing.packages.personal.cta')}
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="ms-2">
                  <path fillRule="evenodd" d="M8.636 3.5a.5.5 0 0 0-.5-.5H1.5A1.5 1.5 0 0 0 0 4.5v10A1.5 1.5 0 0 0 1.5 16h10a1.5 1.5 0 0 0 1.5-1.5V7.864a.5.5 0 0 0-1 0V14.5a.5.5 0 0 1-.5.5h-10a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5h6.636a.5.5 0 0 0 .5-.5z"/>
                  <path fillRule="evenodd" d="M16 .5a.5.5 0 0 0-.5-.5h-5a.5.5 0 0 0 0 1h3.793L6.146 9.146a.5.5 0 1 0 .708.708L15 1.707V5.5a.5.5 0 0 0 1 0v-5z"/>
                </svg>
              </a>
              <Link to={getLocalizedLink('/news')} className="btn btn-secondary">
                {t('home.cta_section.learn_more')}
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16" className="ms-2">
                  <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/>
                </svg>
              </Link>
            </div>
            
            <div className="cta-free-note">
              <FaStar className="note-icon" />
              <span>{t('home.pricing.free_for_ukrainians')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Інші секції сайту */}
      {/* ... existing code ... */}
    </div>
  );
};

export default HomePage; 